/**
 * <NativeUpdateBanner /> (r7.6 step 9) — inside a tenant's Android shell only.
 *   • "Native update required": the OTA manifest says the shell is too old for the current web bundle
 *     (min_native_version) → a persistent bar with "Update on Google Play" (tenant play_url or package).
 *   • "Update ready": a web bundle was downloaded and staged (`snt:ota-staged`) → a dismissable bar with
 *     "Restart now" (CapacitorUpdater.set) — otherwise it applies on the next cold start. Never shown while
 *     a checkout is on screen (/checkout/*), so a payment is never interrupted.
 * Renders nothing on the web.
 */
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isNative, playStoreUrl, otaState } from '@/lib/native';
import { useTenantOptional } from '@/context/TenantContext';

export default function NativeUpdateBanner() {
  const { t } = useTranslation();
  const tenant = useTenantOptional();
  const { pathname } = useLocation();
  const [required, setRequired] = useState(() => { try { return !!sessionStorage.getItem('snt:native:update_required'); } catch { return false; } });
  const [staged, setStaged] = useState(null);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    if (!isNative()) return undefined;
    const onReq = () => setRequired(true);
    const onStaged = (e) => setStaged(e.detail?.version || otaState().pending?.version || 'new');
    window.addEventListener('snt:native-update-required', onReq);
    window.addEventListener('snt:ota-staged', onStaged);
    return () => { window.removeEventListener('snt:native-update-required', onReq); window.removeEventListener('snt:ota-staged', onStaged); };
  }, []);

  if (!isNative() || pathname.startsWith('/checkout')) return null;

  if (required) {
    return (
      <div className="px-4 py-3 text-sm flex items-center gap-3" style={{ background: 'var(--snt-accent)', color: 'var(--snt-accent-fg)' }} data-testid="native-update-required">
        <span className="flex-1">{t('native.update_required', 'A newer version of the app is needed to keep buying tickets.')}</span>
        <a href={playStoreUrl(tenant?.android?.play_url)} className="underline font-medium whitespace-nowrap">{t('native.update_play', 'Update on Google Play')}</a>
      </div>
    );
  }
  if (staged && !hidden) {
    return (
      <div className="px-4 py-2 text-[13px] flex items-center gap-3 border-b border-line" style={{ background: 'var(--snt-bg-1)', color: 'var(--snt-text-2)' }} data-testid="ota-staged">
        <span className="flex-1">{t('native.ota_ready', { v: staged, defaultValue: 'Update {{v}} is ready — it applies next time you open the app.' })}</span>
        <button type="button" className="underline" onClick={async () => { try { const { CapacitorUpdater } = await import('@capgo/capacitor-updater'); const p = otaState().pending; if (p?.id) await CapacitorUpdater.set({ id: p.id }); } catch { /* ignore */ } }}>{t('native.restart_now', 'Restart now')}</button>
        <button type="button" aria-label={t('common.dismiss', 'Dismiss')} onClick={() => setHidden(true)} className="text-lg leading-none">×</button>
      </div>
    );
  }
  return null;
}
